import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dna, Search, Database, Network, BookOpen, Zap, CheckCircle, ArrowRight, Menu, X, Play, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Query builder state
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const exampleQueries = [
    {
      id: 1,
      query: "Find enhancers with activity like VEGFA under hypoxia",
      dimensions: ["Sequence", "Function", "Literature"],
      description: "Searches for regulatory elements with similar expression patterns"
    },
    {
      id: 2,
      query: "Variants functionally similar to BRCA1 loss-of-function",
      dimensions: ["Sequence", "Function", "Network"],
      description: "Identifies variants with comparable pathway disruption"
    },
    {
      id: 3,
      query: "Genes in the same pathway as TP53 with cardiac expression",
      dimensions: ["Network", "Function", "Literature"],
      description: "Finds pathway members with tissue-specific activity"
    }
  ];

  const handleQueryDemo = (query) => {
    setSelectedQuery(query);
    setIsAnalyzing(true);
    setTimeout(() => {
      setIsAnalyzing(false);
    }, 2000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.status === 201) {
        toast.success('Thank you! We will contact you soon.');
        setFormData({ name: '', email: '', organization: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>BioSemantica | Search Genomes by Meaning, Not Coordinates</title>
        <meta name="description" content="Multi-modal semantic search platform for genomic data. Query genes, variants, and regulatory elements by biological meaning across sequence, function, network, and literature." />
        <meta name="keywords" content="genomics, semantic search, bioinformatics, DNA sequencing, gene search, variant analysis, computational biology, genomic data, biological search" />
        
        {/* Open Graph */}
        <meta property="og:title" content="BioSemantica | Search Genomes by Meaning" />
        <meta property="og:description" content="Revolutionary multi-modal semantic search platform for genomic data. Find biologically similar elements across four dimensions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://biosemantica.com" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1578496479530-799fd6d0803a" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="BioSemantica | Search Genomes by Meaning" />
        <meta name="twitter:description" content="Multi-modal semantic search platform for genomic data" />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1578496479530-799fd6d0803a" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              "name": "BioSemantica",
              "description": "Multi-modal semantic search platform for genomic data",
              "applicationCategory": "BioinformaticsApplication",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "operatingSystem": "Web",
              "provider": {
                "@type": "Organization",
                "name": "BioSemantica"
              }
            }
          `}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
          <div className="container mx-auto px-6 py-4">
            <nav className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Dna className="h-8 w-8 text-emerald-600" />
                <span className="text-2xl font-bold text-gray-900">BioSemantica</span>
              </div>
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-8">
                <a href="#services" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium">Services</a>
                <a href="#demo" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium">Demo</a>
                <a href="#how-it-works" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium">How it Works</a>
                <a href="#about" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium">About</a>
                <a href="#contact" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium">Contact</a>
              </div>
              
              <div className="flex items-center space-x-4">
                <a href="#contact">
                  <Button className="hidden md:inline-flex bg-emerald-600 hover:bg-emerald-700 text-white">
                    Request Access
                  </Button>
                </a>
                
                {/* Mobile Menu Button */}
                <button
                  onClick={toggleMobileMenu}
                  className="md:hidden p-2 text-gray-700 hover:text-emerald-600 transition-colors inline-flex"
                  aria-label="Toggle mobile menu"
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </nav>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
              <div className="container mx-auto px-6 py-4 flex flex-col space-y-4">
                <a href="#services" onClick={closeMobileMenu} className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2">Services</a>
                <a href="#demo" onClick={closeMobileMenu} className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2">Demo</a>
                <a href="#how-it-works" onClick={closeMobileMenu} className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2">How it Works</a>
                <a href="#about" onClick={closeMobileMenu} className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2">About</a>
                <a href="#contact" onClick={closeMobileMenu} className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2">Contact</a>
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white w-full">
                  Request Access
                </Button>
              </div>
            </div>
          )}
        </header>

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-6 relative overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://images.unsplash.com/photo-1578496479530-799fd6d0803a" 
              alt="Genomic sequencing" 
              className="w-full h-full object-cover opacity-10"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-white/70 to-white"></div>
          </div>
          
          <div className="container mx-auto relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                Search genomes by <span className="text-emerald-600">meaning</span>, not coordinates.
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
                The multi-modal semantic search platform for genes, variants, and regulatory elements.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 h-14 min-h-14">
                  Start Searching by Meaning
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 text-lg px-8 h-14 min-h-14"
                  onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
                >
                  Try Interactive Demo
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-20 px-6 bg-gray-50">
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Biology doesn't work in coordinates.
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Every genomics tool answers <span className="font-semibold">"what's at this position?"</span> — none answers <span className="font-semibold text-emerald-600">"where are things like this?"</span>
              </p>
              <div className="grid md:grid-cols-3 gap-6 mt-12">
                <Card className="border-2 hover:border-emerald-600 transition-all">
                  <CardHeader>
                    <div className="h-12 w-12 rounded-lg bg-red-100 flex items-center justify-center mb-4">
                      <Search className="h-6 w-6 text-red-600" />
                    </div>
                    <CardTitle className="text-left">Position-Based</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-left">Traditional tools search by chromosomal coordinates, missing biological context.</p>
                  </CardContent>
                </Card>
                <Card className="border-2 hover:border-emerald-600 transition-all">
                  <CardHeader>
                    <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center mb-4">
                      <Database className="h-6 w-6 text-amber-600" />
                    </div>
                    <CardTitle className="text-left">Manual Curation</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-left">Researchers spend days manually curating data to find biologically similar elements.</p>
                  </CardContent>
                </Card>
                <Card className="border-2 hover:border-emerald-600 transition-all">
                  <CardHeader>
                    <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                      <Network className="h-6 w-6 text-purple-600" />
                    </div>
                    <CardTitle className="text-left">Fragmented Data</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-left">Sequence, function, pathways, and literature exist in separate silos.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Query Builder Demo */}
        <section id="demo" className="py-20 px-6 bg-white">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full mb-4">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-semibold">Interactive Demo</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                See BioSemantica in Action
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Try example queries to see how BioSemantica interprets biological questions and searches across multiple dimensions.
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {exampleQueries.map((item) => (
                  <Card 
                    key={item.id}
                    className={`cursor-pointer transition-all border-2 ${
                      selectedQuery?.id === item.id ? 'border-emerald-600 bg-emerald-50' : 'hover:border-emerald-600'
                    }`}
                    onClick={() => handleQueryDemo(item)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        <Play className="h-5 w-5 text-emerald-600" />
                      </div>
                      <CardTitle className="text-base leading-tight">{item.query}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {item.dimensions.map((dim, idx) => (
                          <span key={idx} className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                            {dim}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Query Analysis Display */}
              {selectedQuery && (
                <Card className="border-2 border-emerald-600 shadow-xl">
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Query Analysis</h3>
                    
                    {isAnalyzing ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                          <div className="animate-spin h-12 w-12 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                          <p className="text-gray-600">Analyzing query across multiple dimensions...</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Query Understanding:</h4>
                          <p className="text-gray-700 bg-gray-50 p-4 rounded-lg italic">"{selectedQuery.query}"</p>
                        </div>

                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">Search Dimensions:</h4>
                          <div className="grid md:grid-cols-2 gap-4">
                            {selectedQuery.dimensions.map((dim, idx) => (
                              <div key={idx} className="flex items-center space-x-3 bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                                <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                                <div>
                                  <p className="font-semibold text-gray-900">{dim} Similarity</p>
                                  <p className="text-sm text-gray-600">Active</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-lg border border-emerald-200">
                          <h4 className="font-semibold text-gray-900 mb-2">Expected Results:</h4>
                          <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start space-x-2">
                              <ArrowRight className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                              <span>Coordinate-grounded genomic regions (GRCh38)</span>
                            </li>
                            <li className="flex items-start space-x-2">
                              <ArrowRight className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                              <span>Similarity scores across {selectedQuery.dimensions.length} dimensions</span>
                            </li>
                            <li className="flex items-start space-x-2">
                              <ArrowRight className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                              <span>Evidence-backed results with literature citations</span>
                            </li>
                            <li className="flex items-start space-x-2">
                              <ArrowRight className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                              <span>Tool-ready output for downstream analysis</span>
                            </li>
                          </ul>
                        </div>

                        <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                          Request Full Platform Access
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 px-6 bg-gray-50">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Four dimensions of biological similarity, <span className="text-emerald-600">one search</span>.
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Sequence. Function. Network. Literature. Unified retrieval with coordinate-grounded results.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="relative overflow-hidden group hover:shadow-xl transition-all border-2 hover:border-emerald-600">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-100 rounded-full -mr-16 -mt-16 opacity-50 group-hover:opacity-100 transition-opacity"></div>
                <CardHeader>
                  <div className="h-14 w-14 rounded-xl bg-emerald-100 flex items-center justify-center mb-4 relative z-10">
                    <Dna className="h-8 w-8 text-emerald-600" />
                  </div>
                  <CardTitle className="text-xl">Sequence Similarity</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    DNA language models capture deep sequence patterns beyond BLAST-level alignment.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden group hover:shadow-xl transition-all border-2 hover:border-emerald-600">
                <div className="absolute top-0 right-0 w-32 h-32 bg-teal-100 rounded-full -mr-16 -mt-16 opacity-50 group-hover:opacity-100 transition-opacity"></div>
                <CardHeader>
                  <div className="h-14 w-14 rounded-xl bg-teal-100 flex items-center justify-center mb-4 relative z-10">
                    <Zap className="h-8 w-8 text-teal-600" />
                  </div>
                  <CardTitle className="text-xl">Functional Similarity</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    Regulatory prediction models find elements with similar epigenomic and chromatin signatures.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden group hover:shadow-xl transition-all border-2 hover:border-emerald-600">
                <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-100 rounded-full -mr-16 -mt-16 opacity-50 group-hover:opacity-100 transition-opacity"></div>
                <CardHeader>
                  <div className="h-14 w-14 rounded-xl bg-cyan-100 flex items-center justify-center mb-4 relative z-10">
                    <Network className="h-8 w-8 text-cyan-600" />
                  </div>
                  <CardTitle className="text-xl">Network Context</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    Knowledge graph embeddings reveal pathway relationships and molecular interactions.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden group hover:shadow-xl transition-all border-2 hover:border-emerald-600">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full -mr-16 -mt-16 opacity-50 group-hover:opacity-100 transition-opacity"></div>
                <CardHeader>
                  <div className="h-14 w-14 rounded-xl bg-blue-100 flex items-center justify-center mb-4 relative z-10">
                    <BookOpen className="h-8 w-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-xl">Literature Evidence</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    Biomedical text embeddings connect genomic elements to 35 million research abstracts.
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* How it Works Section */}
        <section id="how-it-works" className="py-20 px-6 bg-white">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                From biological question to actionable result.
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Natural language in. GRCh38 coordinates, evidence, and tool-ready output out.
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="h-16 w-16 rounded-full bg-emerald-600 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    1
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Ask in Natural Language</h3>
                  <p className="text-gray-600">
                    "Find enhancers with activity like VEGFA under hypoxia" or "Variants functionally similar to BRCA1 loss-of-function"
                  </p>
                </div>

                <div className="text-center">
                  <div className="h-16 w-16 rounded-full bg-teal-600 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    2
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Multi-Modal Search</h3>
                  <p className="text-gray-600">
                    Query across sequence, regulatory function, pathway context, and clinical literature simultaneously
                  </p>
                </div>

                <div className="text-center">
                  <div className="h-16 w-16 rounded-full bg-cyan-600 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    3
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Coordinate-Grounded Results</h3>
                  <p className="text-gray-600">
                    Get GRCh38 coordinates, evidence scores, and tool-ready outputs that plug into your existing pipelines
                  </p>
                </div>
              </div>

              <div className="mt-16 relative">
                <img 
                  src="https://images.pexels.com/photos/97077/pexels-photo-97077.jpeg" 
                  alt="Computational biology workflow" 
                  className="w-full h-80 object-cover rounded-xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent rounded-xl"></div>
                <div className="absolute bottom-8 left-8 text-white">
                  <h4 className="text-2xl font-bold mb-2">Seamless Integration</h4>
                  <p className="text-white/90">Augments your existing genomic analysis tools</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Data Section */}
        <section className="py-20 px-6 bg-gray-50">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Six layers of genomics, <span className="text-emerald-600">harmonized</span>.
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Reference genome, regulatory elements, variation, expression, pathways, and 35 million abstracts — all indexed and searchable.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                { title: 'Reference Genome', desc: 'GRCh38 complete assembly' },
                { title: 'Regulatory Elements', desc: 'ENCODE & Roadmap Epigenomics' },
                { title: 'Genetic Variation', desc: 'ClinVar, gnomAD, dbSNP' },
                { title: 'Gene Expression', desc: 'GTEx, TCGA, single-cell atlases' },
                { title: 'Pathway Data', desc: 'Reactome, GO, KEGG' },
                { title: 'Literature', desc: '35M+ PubMed abstracts' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-4 p-6 rounded-xl bg-white hover:bg-emerald-50 transition-colors border border-gray-200 hover:border-emerald-600">
                  <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 px-6 bg-white">
          <div className="container mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              <div>
                <img 
                  src="https://images.pexels.com/photos/8851630/pexels-photo-8851630.jpeg" 
                  alt="Research team" 
                  className="w-full h-96 object-cover rounded-xl shadow-2xl"
                />
              </div>
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  Not another annotation tool.
                </h2>
                <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                  BioSemantica doesn't look up what's known. It finds what's <span className="font-semibold text-emerald-600">biologically similar</span> — across dimensions no existing tool can search.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-700">
                      <span className="font-semibold">Entity-level search:</span> Query individual genes, variants, and regulatory elements
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-700">
                      <span className="font-semibold">Multi-modal fusion:</span> Combine sequence, function, network, and text simultaneously
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-700">
                      <span className="font-semibold">Coordinate-grounded:</span> Results map directly to GRCh38 for seamless integration
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-700">
                      <span className="font-semibold">Intent-driven:</span> Understands biological questions, not just keyword matching
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 px-6 bg-gray-50">
          <div className="container mx-auto">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  Request Early Access
                </h2>
                <p className="text-xl text-gray-600">
                  Join leading researchers transforming genomic discovery. Get in touch to learn more about BioSemantica.
                </p>
              </div>

              <Card className="border-2 shadow-xl">
                <CardContent className="pt-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-gray-700 font-medium">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="mt-2 border-gray-300 focus:border-emerald-600 focus:ring-emerald-600"
                        placeholder="Dr. Jane Smith"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-gray-700 font-medium">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="mt-2 border-gray-300 focus:border-emerald-600 focus:ring-emerald-600"
                        placeholder="jane.smith@university.edu"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <Label htmlFor="organization" className="text-gray-700 font-medium">Organization</Label>
                      <Input
                        id="organization"
                        name="organization"
                        type="text"
                        value={formData.organization}
                        onChange={handleInputChange}
                        className="mt-2 border-gray-300 focus:border-emerald-600 focus:ring-emerald-600"
                        placeholder="Research Institution or Company"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-gray-700 font-medium">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        rows={4}
                        value={formData.message}
                        onChange={handleInputChange}
                        className="mt-2 border-gray-300 focus:border-emerald-600 focus:ring-emerald-600"
                        placeholder="Tell us about your research interests and use case..."
                        disabled={isSubmitting}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-lg py-6"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                      {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12 px-6">
          <div className="container mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Dna className="h-8 w-8 text-emerald-400" />
                  <span className="text-xl font-bold">BioSemantica</span>
                </div>
                <p className="text-gray-400 text-sm">
                  Multi-modal semantic search for genomic data.
                </p>
              </div>

              <div>
                <h4 className="font-bold mb-4">Platform</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li><a href="#services" className="hover:text-emerald-400 transition-colors">Services</a></li>
                  <li><a href="#demo" className="hover:text-emerald-400 transition-colors">Interactive Demo</a></li>
                  <li><a href="#how-it-works" className="hover:text-emerald-400 transition-colors">How it Works</a></li>
                  <li><a href="#" className="hover:text-emerald-400 transition-colors">Documentation</a></li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold mb-4">Company</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li><a href="#about" className="hover:text-emerald-400 transition-colors">About</a></li>
                  <li><a href="#" className="hover:text-emerald-400 transition-colors">Research</a></li>
                  <li><a href="#contact" className="hover:text-emerald-400 transition-colors">Contact</a></li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold mb-4">Resources</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li><a href="#" className="hover:text-emerald-400 transition-colors">Case Studies</a></li>
                  <li><a href="#" className="hover:text-emerald-400 transition-colors">Publications</a></li>
                  <li><a href="#" className="hover:text-emerald-400 transition-colors">API Access</a></li>
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
              <p>&copy; 2025 BioSemantica. All rights reserved. Search genomes by meaning, not coordinates.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Home;
